import { isArray } from 'lodash';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { paramsKey } from 'src/components/filterList';
import { type IFilter } from 'src/interfaces';
import { mapEscapeRegExWithExclusions, undoEscapedRegExp } from 'src/utils';

import { useParamQuery } from './useParamQuery';

export const useFilterAndSorting = (
  filters?: IFilter[],
  excludedChars: string[] = [],
  isFuzzySearch: boolean = true,
  setIsFuzzySearch: Dispatch<SetStateAction<boolean>> = () => {},
  skipEscapeRegex: boolean = false,
) => {
  const { activeFilters, getParamsValue, onChangeParams } = useParamQuery();

  //searching
  const onChangeKeyword = useCallback(
    (data: string | string[]) => {
      const keywords = isArray(data) ? data : [data];

      const useExactMatch = !isFuzzySearch;

      const processedKeywords = keywords
        .map((keyword) => {
          if (!keyword) return keyword;

          return skipEscapeRegex || useExactMatch
            ? keyword
            : mapEscapeRegExWithExclusions(keyword, excludedChars);
        })
        .filter((keyword): keyword is string => Boolean(keyword));

      onChangeParams(
        `${useExactMatch ? 'name.list' : 'name.regexp'}`,
        processedKeywords,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChangeParams, isFuzzySearch],
  );

  const onRemoveKeyword = useCallback(
    (value: string) => {
      const regexpTerms = getParamsValue('name.regexp').filter(
        (name) => name !== value,
      );
      const listTerms = getParamsValue('name.list').filter(
        (name) => name !== value,
      );

      onChangeParams('name.regexp', regexpTerms);
      onChangeParams('name.list', listTerms);

      if (regexpTerms.length === 0 && listTerms.length === 0) {
        setIsFuzzySearch(true);
      }
    },
    [getParamsValue, onChangeParams, setIsFuzzySearch],
  );

  const onRemovePublicationKeyword = useCallback(
    (value: string) => {
      const newKeywordArray = getParamsValue(
        `${
          isFuzzySearch
            ? 'hq.name_or_store_number.regexp'
            : 'hq.name_or_store_number.list'
        }`,
      ).filter((name) => name !== value);
      onChangeParams(
        `${
          isFuzzySearch
            ? 'hq.name_or_store_number.regexp'
            : 'hq.name_or_store_number.list'
        }`,
        newKeywordArray,
      );
    },
    [getParamsValue, onChangeParams, isFuzzySearch],
  );

  const onRemoveProductKeyword = useCallback(
    (value: string) => {
      const newKeywordArray = getParamsValue(
        `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
      ).filter((name) => name !== value);
      onChangeParams(
        `${isFuzzySearch ? 'display_name.filter' : 'display_name.list'}`,
        newKeywordArray,
      );
    },
    [getParamsValue, onChangeParams, isFuzzySearch],
  );

  const onChangeFilter = useCallback(
    (key: string, value: string | string[]) => {
      onChangeParams(key, value);

      if (key === 'name.regexp' || key === 'name.list') {
        const normalizedValue = Array.isArray(value)
          ? value
          : value
          ? [value]
          : [];
        const otherKey = key === 'name.regexp' ? 'name.list' : 'name.regexp';
        const otherValues = getParamsValue(otherKey);

        if (normalizedValue.length === 0 && otherValues.length === 0) {
          setIsFuzzySearch(true);
        }
      }
    },
    [getParamsValue, onChangeParams, setIsFuzzySearch],
  );

  const onChangeToExactMatch = (
    fromKey: string,
    toKey: string,
    pageSize?: string,
  ) => {
    const activeParams = getParamsValue(fromKey);
    onChangeParams(
      toKey,
      activeParams.map((escapedString) => undoEscapedRegExp(escapedString)),
    );
    onChangeParams(fromKey, []);
    pageSize && onChangeParams('pageSize', pageSize);
  };

  const onChangeToFuzzyMatch = (
    fromKey: string,
    toKey: string,
    pageSize?: string,
  ) => {
    const activeParams = getParamsValue(fromKey);
    onChangeParams(
      toKey,
      skipEscapeRegex
        ? activeParams
        : mapEscapeRegExWithExclusions(activeParams, excludedChars),
    );
    onChangeParams(fromKey, []);
    pageSize && onChangeParams('pageSize', pageSize);
  };

  const onReset = useCallback(() => {
    onChangeParams(`${isFuzzySearch ? 'name.regexp' : 'name.list'}`, []);
    onChangeParams('display_name.filter', []);
    onChangeParams('display_name.list', []);
    onChangeParams('hq.name_or_store_number.regexp', []);
    filters?.forEach(({ label }) => {
      onChangeParams(paramsKey(label), []);
    });
    setIsFuzzySearch(true);
  }, [filters, onChangeParams, isFuzzySearch, setIsFuzzySearch]);

  return {
    activeFilters,
    getParamsValue,
    onChangeParams,
    onChangeKeyword,
    onRemoveKeyword,
    onRemovePublicationKeyword,
    onRemoveProductKeyword,
    onChangeFilter,
    onReset,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
  };
};
